import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Button,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormGroup,
  Checkbox,
  Switch,
  Paper
} from '@mui/material';
import { 
  Schedule, 
  AccessTime, 
  DirectionsCar, 
  LocationOn,
  CalendarMonth,
  Warning,
  CheckCircle,
  Info
} from '@mui/icons-material';

const AvailabilityStep = ({ formData, updateFormData, errors }) => {
  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const timeSlots = [
    { value: 'early_morning', label: 'Early Morning (6 AM - 9 AM)' },
    { value: 'morning', label: 'Morning (9 AM - 12 PM)' },
    { value: 'afternoon', label: 'Afternoon (12 PM - 5 PM)' },
    { value: 'evening', label: 'Evening (5 PM - 8 PM)' },
    { value: 'flexible', label: 'Flexible - Any time' }
  ];

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    updateFormData({ [field]: value });
  };

  const handleRadioChange = (field) => (event) => {
    const value = event.target.value === 'true';
    updateFormData({ [field]: value });
  };

  const handleSwitchChange = (field) => (event) => {
    const value = event.target.checked;
    updateFormData({ [field]: value });
  };

  const handleDayAvailabilityChange = (day) => (event) => {
    const isChecked = event.target.checked;
    const currentAvailability = formData.weeklyAvailability || {};
    
    updateFormData({ 
      weeklyAvailability: {
        ...currentAvailability,
        [day]: isChecked
      }
    });
  };

  const handleTimeSlotChange = (timeSlot) => (event) => {
    const isChecked = event.target.checked;
    const currentTimeSlots = formData.preferredTimeSlots || [];
    
    if (isChecked) {
      updateFormData({ 
        preferredTimeSlots: [...currentTimeSlots, timeSlot] 
      });
    } else {
      updateFormData({ 
        preferredTimeSlots: currentTimeSlots.filter(slot => slot !== timeSlot) 
      });
    }
  };

  const getAvailableDaysCount = () => {
    const availability = formData.weeklyAvailability || {};
    return Object.values(availability).filter(Boolean).length;
  };

  const getTimeSlotDescription = (slot) => {
    const descriptions = {
      'early_morning': 'Great for clients who want cleaning before work',
      'morning': 'Most popular time slot for residential cleaning',
      'afternoon': 'Good for office cleaning and larger homes',
      'evening': 'Perfect for busy professionals',
      'flexible': 'Maximum booking opportunities'
    };
    return descriptions[slot] || '';
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Availability & Transportation
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Set your work schedule and transportation details to match you with suitable cleaning jobs.
      </Typography>

      {/* Weekly Availability */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonth color="primary" /> Weekly Availability
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Select the days you are available to work. More availability means more booking opportunities.
          </Typography>

          <Grid container spacing={2}>
            {daysOfWeek.map((day) => (
              <Grid item xs={6} sm={4} md={3} key={day.key}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    border: 1,
                    borderColor: (formData.weeklyAvailability || {})[day.key] ? 'primary.main' : 'grey.300',
                    backgroundColor: (formData.weeklyAvailability || {})[day.key] ? 'primary.light' : 'background.paper'
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(formData.weeklyAvailability || {})[day.key] || false}
                        onChange={handleDayAvailabilityChange(day.key)}
                        color="primary"
                      />
                    }
                    label={day.label}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>

          {getAvailableDaysCount() > 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <CheckCircle sx={{ verticalAlign: 'middle', mr: 1 }} />
                Great! You're available {getAvailableDaysCount()} day{getAvailableDaysCount() > 1 ? 's' : ''} per week.
                {getAvailableDaysCount() >= 5 && ' This gives you maximum booking flexibility!'}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Preferred Time Slots */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTime color="primary" /> Preferred Time Slots
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Choose your preferred working hours. Clients book services throughout the day.
          </Typography>

          <Grid container spacing={2}>
            {timeSlots.map((timeSlot) => (
              <Grid item xs={12} key={timeSlot.value}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    border: (formData.preferredTimeSlots || []).includes(timeSlot.value) ? 2 : 1,
                    borderColor: (formData.preferredTimeSlots || []).includes(timeSlot.value) ? 'primary.main' : 'grey.300'
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={(formData.preferredTimeSlots || []).includes(timeSlot.value)}
                              onChange={handleTimeSlotChange(timeSlot.value)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {timeSlot.label}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {getTimeSlotDescription(timeSlot.value)}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Work Schedule Preferences */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule color="primary" /> Work Schedule Preferences
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Hours per Week</InputLabel>
                <Select
                  value={formData.hoursPerWeek || ''}
                  onChange={handleChange('hoursPerWeek')}
                  label="Hours per Week"
                >
                  <MenuItem value="part_time_10">Part-time (10-20 hours)</MenuItem>
                  <MenuItem value="part_time_20">Part-time (20-30 hours)</MenuItem>
                  <MenuItem value="full_time">Full-time (30-40 hours)</MenuItem>
                  <MenuItem value="flexible">Flexible - varies by week</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Notice Required</InputLabel>
                <Select
                  value={formData.noticeRequired || ''}
                  onChange={handleChange('noticeRequired')}
                  label="Notice Required"
                >
                  <MenuItem value="same_day">Same day (very flexible)</MenuItem>
                  <MenuItem value="one_day">1 day advance notice</MenuItem>
                  <MenuItem value="two_days">2 days advance notice</MenuItem>
                  <MenuItem value="week">1 week advance notice</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Available for Emergency/Last-Minute Bookings
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Emergency bookings often pay premium rates
                  </Typography>
                </Box>
                <Switch
                  checked={formData.emergencyAvailable || false}
                  onChange={handleSwitchChange('emergencyAvailable')}
                  color="primary"
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Available for Weekend Work
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Weekend slots are in high demand
                  </Typography>
                </Box>
                <Switch
                  checked={formData.weekendAvailable || false}
                  onChange={handleSwitchChange('weekendAvailable')}
                  color="primary"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Transportation Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsCar color="primary" /> Transportation & Equipment
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl error={!!errors.hasVehicle} required>
                <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>
                  Do you have your own reliable vehicle?
                </FormLabel>
                <RadioGroup
                  value={formData.hasVehicle === null ? '' : formData.hasVehicle.toString()}
                  onChange={handleRadioChange('hasVehicle')}
                  row
                >
                  <FormControlLabel 
                    value="true" 
                    control={<Radio />} 
                    label="Yes, I have my own vehicle" 
                  />
                  <FormControlLabel 
                    value="false" 
                    control={<Radio />} 
                    label="No, I need transportation assistance" 
                  />
                </RadioGroup>
                {errors.hasVehicle && (
                  <Typography color="error" variant="caption">
                    {errors.hasVehicle}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {formData.hasVehicle && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Vehicle Type"
                    placeholder="e.g., Sedan, SUV, Van, Truck"
                    value={formData.vehicleType || ''}
                    onChange={handleChange('vehicleType')}
                    helperText="What type of vehicle do you drive?"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Can Transport Large Equipment
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Vacuum cleaners, carpet cleaners, etc.
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.canTransportEquipment || false}
                      onChange={handleSwitchChange('canTransportEquipment')}
                      color="primary"
                    />
                  </Box>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControl error={!!errors.hasCleaningSupplies}>
                <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>
                  Do you have your own basic cleaning supplies?
                </FormLabel>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Basic supplies include: all-purpose cleaner, glass cleaner, disinfectant, microfiber cloths, etc.
                </Typography>
                <RadioGroup
                  value={formData.hasCleaningSupplies === null ? '' : formData.hasCleaningSupplies.toString()}
                  onChange={handleRadioChange('hasCleaningSupplies')}
                  row
                >
                  <FormControlLabel 
                    value="true" 
                    control={<Radio />} 
                    label="Yes, I have my own supplies" 
                  />
                  <FormControlLabel 
                    value="false" 
                    control={<Radio />} 
                    label="No, I need supplies provided" 
                  />
                </RadioGroup>
                {errors.hasCleaningSupplies && (
                  <Typography color="error" variant="caption">
                    {errors.hasCleaningSupplies}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Availability Summary */}
      {(getAvailableDaysCount() > 0 || (formData.preferredTimeSlots || []).length > 0) && (
        <Box sx={{ mt: 3, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.dark">
            <Info sx={{ verticalAlign: 'middle', mr: 1 }} />
            <strong>Availability Summary:</strong> {getAvailableDaysCount()} days per week, 
            {(formData.preferredTimeSlots || []).length} time slot{(formData.preferredTimeSlots || []).length !== 1 ? 's' : ''} selected.
            {(formData.emergencyAvailable || formData.weekendAvailable) && 
              ' Plus availability for ' + 
              [formData.emergencyAvailable && 'emergency', formData.weekendAvailable && 'weekend'].filter(Boolean).join(' and ') + 
              ' work.'
            }
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AvailabilityStep;